export const defaultLocale = 'en-US';

export interface LocaleEntry {
  default?: Record<string, string>;
}

const defaultLocaleImport = import('./en-US.json');

export const resolveLocales =
  (...localeImports) =>
  async (): Promise<LocaleEntry> => {
    const resolvedImports = await Promise.all([defaultLocaleImport, ...localeImports]);
    return resolvedImports.reduce(
      (acc, value) => ({
        ...acc,
        ...value,
        default: {
          ...acc.default,
          ...value.default
        }
      }),
      {}
    );
  };

export const localeToParentLocaleIdMap = {
  'en-US': 1033,
  'de-DE': 1031,
  'es-ES': 3082,
  'fi-FI': 1035,
  'fr-CA': 3084,
  'fr-FR': 1036,
  'it-IT': 1040,
  'ja-JP': 1041,
  'ko-KR': 1042,
  'nl-NL': 1043,
  'pt-BR': 1046,
  'pt-PT': 2070,
  'ru-RU': 1049,
  'sv-SE': 1053,
  'th-TH': 1054,
  'tr-TR': 1055,
  'zh-Hans': 4,
  'zh-CN': 2052,
  'zh-SG': 4100,
  'zh-CHS': 4,
  'zh-Hant': 31748,
  'zh-CHT': 31748,
  'zh-HK': 3076,
  'zh-MO': 5124,
  'zh-TW': 1028
};

const locales = {
  'en-US': resolveLocales(defaultLocaleImport),
  'de-DE': resolveLocales(defaultLocaleImport, import('./de-DE.json')),
  'es-ES': resolveLocales(defaultLocaleImport, import('./es-ES.json')),
  'fi-FI': resolveLocales(defaultLocaleImport, import('./fi-FI.json')),
  'fr-CA': resolveLocales(defaultLocaleImport, import('./fr-CA.json')),
  'fr-FR': resolveLocales(defaultLocaleImport, import('./fr-FR.json')),
  'it-IT': resolveLocales(defaultLocaleImport, import('./it-IT.json')),
  'ja-JP': resolveLocales(defaultLocaleImport, import('./ja-JP.json')),
  'ko-KR': resolveLocales(defaultLocaleImport, import('./ko-KR.json')),
  'nl-NL': resolveLocales(defaultLocaleImport, import('./nl-NL.json')),
  'pt-BR': resolveLocales(defaultLocaleImport, import('./pt-BR.json')),
  'pt-PT': resolveLocales(defaultLocaleImport, import('./pt-PT.json')),
  'ru-RU': resolveLocales(defaultLocaleImport, import('./ru-RU.json')),
  'sv-SE': resolveLocales(defaultLocaleImport, import('./sv-SE.json')),
  'th-TH': resolveLocales(defaultLocaleImport, import('./th-TH.json')),
  'tr-TR': resolveLocales(defaultLocaleImport, import('./tr-TR.json')),
  'zh-Hans': resolveLocales(defaultLocaleImport, import('./zh-Hans.json')),
  'zh-CN': resolveLocales(defaultLocaleImport, import('./zh-Hans.json')),
  'zh-SG': resolveLocales(defaultLocaleImport, import('./zh-Hans.json')),
  'zh-CHS': resolveLocales(defaultLocaleImport, import('./zh-Hans.json')),
  'zh-Hant': resolveLocales(defaultLocaleImport, import('./zh-Hant.json')),
  'zh-CHT': resolveLocales(defaultLocaleImport, import('./zh-Hant.json')),
  'zh-HK': resolveLocales(defaultLocaleImport, import('./zh-Hant.json')),
  'zh-MO': resolveLocales(defaultLocaleImport, import('./zh-Hant.json')),
  'zh-TW': resolveLocales(defaultLocaleImport, import('./zh-Hant.json'))
};

export default locales;

const getLocaleShortCode = (locale = ''): string => locale.split('-')[0] || '';

export const supportedLocales = Object.keys(locales);

const prepareShortCodeToParentMapping = (): Record<string, string> => {
  const mapping = {};
  supportedLocales.forEach(locale => {
    const shortCode = getLocaleShortCode(locale);
    if (mapping[shortCode]) {
      delete mapping[shortCode];
    } else {
      mapping[shortCode] = locale;
    }
  });
  return mapping;
};

export const shortCodeToParentMapping = prepareShortCodeToParentMapping();

const getLocaleResources = async (locale: string = defaultLocale): Promise<unknown> => {
  const localesRespource = await locales[locale]();
  return localesRespource;
};

export const getLocaleIdByLocaleName = (localeName: string): number => {
  const parentLocaleId = localeToParentLocaleIdMap[localeName];
  return parentLocaleId || -1;
};

export const convertToSupportedLocale = (locale: string): string => {
  const supportedLocale = supportedLocales.find(supported => supported === locale);
  if (supportedLocale) {
    return supportedLocale;
  }
  const shortCode = getLocaleShortCode(locale);
  const parentLocale = shortCodeToParentMapping[shortCode];
  if (parentLocale && supportedLocales.includes(parentLocale)) {
    return parentLocale;
  }
  return defaultLocale;
};

/**
 * Get the text resources for the given locale
 */
export function buildLocales(userLocale = defaultLocale): unknown {
  const localeData = {};
  const supportedLocale = convertToSupportedLocale(userLocale);
  localeData[userLocale] = (): unknown => getLocaleResources(supportedLocale);
  return localeData;
}
export const switchChineseLocale = (locale: string): string => {
  // Intl.DateTimeFormat does not work with zh-cht nor zh-chs
  switch (locale.toLowerCase()) {
    case 'zh-cht':
      return 'zh-Hant';
    case 'zh-chs':
      return 'zh-Hans';
    default:
      return locale;
  }
};

/**
 * translate function override that falls back to the key if the original function throws an error
 */
export const translate = (resolveTranslation, key, ...args): string => {
  try {
    return resolveTranslation(key, ...args);
  } catch {
    return key;
  }
};
